import { SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { nbnProductService } from 'app/nbn/services/nbn-product/nbn-product.service';
import { changePlanService } from 'app/nbn/services/change-plan/change-plan.service';
import { NbnProductsResponse } from 'app/nbn/models/nbn-products-response.model';
import { addAddressHandler } from 'app/nbn/store/handlers/address.handler';
import { customerProfileHandler } from 'app/nbn/store/handlers/customerProfile.handler';
import { bannerService, featureService, flowService, productsSessionStorageService } from 'app/shared/services/index';
import { removeHandler } from 'app/shared/store/handlers/types/remove-handler';
import { AddressDetailModel, FeatureKey, SelectedProductSessionData } from 'app/shared/models/index';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { setPromoInStore } from 'app/shared/utilities/promo.util';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';

const registeredHandlers = (): removeHandler => {
  const removeAddressHandler = addAddressHandler(store);
  const removeTelcoAccountHandler = customerProfileHandler(store);
  return () => {
    removeAddressHandler();
    removeTelcoAccountHandler();
  };
};

export const initProductDetails = async (address: AddressDetailModel, customerLocationId: string): Promise<void> => {
  let getProductPromise: Promise<NbnProductsResponse>;
  let resetAddressStore = false;

  if (customerLocationId) {
    getProductPromise = nbnProductService.getProductsByLocationId(customerLocationId);
    // check if the address is already in store, otherwise reset address.
    resetAddressStore = !productsSessionStorageService.getNbnProductsByLocationId<NbnProductsResponse>(customerLocationId);
  } else getProductPromise = nbnProductService.getProducts(address);

  await getProductPromise
    .then(async (productDetails) => {
      setPromoInStore(productDetails);
      store.set(StoreKeys.productDetails, productDetails);
      await nbnProductService.getVoipProducts(address?.state).then((voipProductDetails) => {
        store.set('voipProductDetails', voipProductDetails);
      });
      await nbnProductService.getModemProducts(address?.state).then((modemProductDetails) => {
        store.set('modemProductDetails', modemProductDetails);
      });

      if (resetAddressStore) {
        address = productDetails.serviceQualification?.nbn?.nbnLocation[0].addressStructured;
        if (address) {
          address.fullAddress = productDetails.serviceQualification?.nbn?.nbnLocation[0].addressLong;
          store.set('address', address);
        }
      }

      store.set('responseError', null);
      bannerService.toggleBanner();
    })
    .catch((error) => {
      if (error?.name === 'AbortError') {
        store.set('responseError', null);
      } else {
        store.set('responseError', error);
      }
    })
    .finally(() => {
      store.set('isPageLoaded', true);
    });
};

export const onStoreInit = async (isGuidedFlow: boolean): Promise<void> => {
  if (!isGuidedFlow && sessionStorage.getItem(SessionStorageKeys.FlowDetails) && flowService.getFlowDetails()?.flowType === 'guided-flow') {
    sessionStorage.removeItem(SessionStorageKeys.FlowDetails);
    sessionStorage.removeItem(SessionStorageKeys.PageHistory);
  }

  // Get feature flag
  const featureFlags = await featureService.loadFeaturesStatus([FeatureKey.NBN_MOVERS_ENABLED]);
  if (featureFlags?.nbnMoversEnabled && sessionStorage.getItem('appConfig') === 'movers') {
    store.set('isNbnMovers', true);
  }

  const productSessionData = productsSessionStorageService.getLatestProductsByProductCategory('nbn');
  if (productSessionData?.address) {
    productSessionData.address.fullAddress = productSessionData.key;
    store.set('address', productSessionData.address);
  }

  const selectedProductString = sessionStorage.getItem(SessionStorageKeys.selectedProductDetails);
  if (selectedProductString && selectedProductString.length > 0) {
    const selectedNbnProduct: SelectedProductSessionData = JSON.parse(selectedProductString);
    if (selectedNbnProduct) {
      const serviceQualification = Array.isArray(selectedNbnProduct.productDetails)
        ? selectedNbnProduct.productDetails[0].serviceQualification
        : (selectedNbnProduct.productDetails as any).serviceQualification;
      if (serviceQualification?.nbn) {
        store.set(StoreKeys.selectedNbnProductDetails, selectedNbnProduct);
      }
    }
  }

  registeredHandlers();

  const customerProfile = customerProfileService.getCustomerProfile();
  let locationId: string;
  const isChangingPlan = changePlanService.isChangePlan();
  if (customerProfile && isChangingPlan) {
    locationId = customerProfile.locationId;
    store.set(StoreKeys.customerProfile, customerProfile);
  }

  const energyContractString = sessionStorage.getItem('energyExistingContract');
  if (energyContractString && energyContractString.length > 0) {
    const contracts = JSON.parse(energyContractString);
    store.set('contractsData', contracts);
  }

  store.set('responseError', null);
  await initProductDetails(productSessionData?.address, locationId);
};
